// TelcoTrack - Construction Diary API service
// Thin wrapper nad REST endpointy `/api/objects/construction-diary/*`.
// Gateway směruje přes první segment (`objects`), controller je namountován
// pod `@Controller('objects')` s prefixem `construction-diary/` (viz
// `object-work-report.controller.ts` pro stejný pattern).

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ConstructionDiary,
  ConstructionDiaryAttachment,
  ConstructionDiaryListItem,
  DiaryAdditionalSignatureConfirm,
  UpdateConstructionDiaryDto,
} from '../models/construction-diary.model';

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class ConstructionDiaryApiService {
  private readonly apiUrl = `${environment.apiUrl}${environment.objectServiceUrl}/construction-diary`;

  constructor(private readonly http: HttpClient) {}

  /** Získat nebo vytvořit dnešní záznam. */
  getOrCreateToday(polygonId: string, entryDate?: string): Observable<ConstructionDiary> {
    return this.http
      .post<ApiEnvelope<ConstructionDiary>>(`${this.apiUrl}/today`, { polygonId, entryDate })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Všechny deníky v kalendářním rozsahu (web `/diary`). Web posílá jeden pár `dateFrom`/`dateTo`. */
  list(options?: {
    dateFrom?: string;
    dateTo?: string;
    ranges?: { dateFrom: string; dateTo: string }[];
    /** `mine` = stavby, kde je aktuální uživatel přiřazený stavbyvedoucí. */
    scope?: 'all' | 'mine';
  }): Observable<ConstructionDiaryListItem[]> {
    let params = new HttpParams();
    const ranges =
      options?.ranges?.length
        ? options.ranges
        : options?.dateFrom && options?.dateTo
          ? [{ dateFrom: options.dateFrom, dateTo: options.dateTo }]
          : [];
    if (ranges.length === 1) {
      params = params.set('dateFrom', ranges[0]!.dateFrom).set('dateTo', ranges[0]!.dateTo);
    } else if (ranges.length > 1) {
      params = params
        .set('dateFrom', ranges.map((r) => r.dateFrom).join(','))
        .set('dateTo', ranges.map((r) => r.dateTo).join(','));
    }
    if (options?.scope === 'mine') {
      params = params.set('scope', 'mine');
    }
    return this.http
      .get<ApiEnvelope<ConstructionDiaryListItem[]>>(`${this.apiUrl}/list`, { params })
      .pipe(map((r) => this.unwrap(r) ?? []), catchError((e) => this.fail(e)));
  }

  listMine(options?: {
    month?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Observable<ConstructionDiaryListItem[]> {
    let params = new HttpParams();
    if (options?.month) params = params.set('month', options.month);
    if (options?.dateFrom) params = params.set('dateFrom', options.dateFrom);
    if (options?.dateTo) params = params.set('dateTo', options.dateTo);
    return this.http
      .get<ApiEnvelope<ConstructionDiaryListItem[]>>(`${this.apiUrl}/my/list`, { params })
      .pipe(map((r) => this.unwrap(r) ?? []), catchError((e) => this.fail(e)));
  }

  listPreparedToday(): Observable<ConstructionDiaryListItem[]> {
    return this.http
      .get<ApiEnvelope<ConstructionDiaryListItem[]>>(`${this.apiUrl}/today/list`)
      .pipe(map((r) => this.unwrap(r) ?? []), catchError((e) => this.fail(e)));
  }

  getById(id: string): Observable<ConstructionDiary> {
    return this.http
      .get<ApiEnvelope<ConstructionDiary>>(`${this.apiUrl}/${id}`)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  update(id: string, body: UpdateConstructionDiaryDto): Observable<ConstructionDiary> {
    return this.http
      .put<ApiEnvelope<ConstructionDiary>>(`${this.apiUrl}/${id}`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /**
   * Uzamčení deníku. Volitelně `draft` = stejná pole jako `PUT /:id` (uloží se před uzamčením v jednom requestu).
   */
  confirm(
    id: string,
    body: {
      signaturePngBase64: string;
      additionalSignatures?: DiaryAdditionalSignatureConfirm[];
      draft?: UpdateConstructionDiaryDto;
    },
  ): Observable<ConstructionDiary> {
    const payload: Record<string, unknown> = { signaturePngBase64: body.signaturePngBase64 };
    if (body.draft != null) {
      payload['draft'] = body.draft;
    }
    if (body.additionalSignatures?.length) {
      payload['additionalSignatures'] = body.additionalSignatures;
    }
    return this.http
      .post<ApiEnvelope<ConstructionDiary>>(`${this.apiUrl}/${id}/confirm`, payload)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  listAttachments(id: string): Observable<ConstructionDiaryAttachment[]> {
    return this.http
      .get<ApiEnvelope<ConstructionDiaryAttachment[]>>(`${this.apiUrl}/${id}/attachments`)
      .pipe(map((r) => this.unwrap(r) ?? []), catchError((e) => this.fail(e)));
  }

  uploadAttachment(id: string, file: string, fileName: string): Observable<ConstructionDiaryAttachment> {
    return this.http
      .post<ApiEnvelope<ConstructionDiaryAttachment>>(`${this.apiUrl}/${id}/attachments`, { file, fileName })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  deleteAttachment(id: string, attachmentId: string): Observable<void> {
    return this.http
      .delete<ApiEnvelope<void>>(`${this.apiUrl}/${id}/attachments/${attachmentId}`)
      .pipe(map(() => undefined), catchError((e) => this.fail(e)));
  }

  downloadAttachment(attachmentId: string): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/attachments/${attachmentId}/download`, { responseType: 'blob' })
      .pipe(catchError((e) => this.fail(e)));
  }

  private unwrap<T>(res: ApiEnvelope<T>): T {
    if (res?.success && res.data !== undefined) return res.data;
    throw new Error(res?.message || 'Neplatná odpověď serveru.');
  }

  private fail(err: unknown): Observable<never> {
    return throwError(() => new Error(this.extractApiErrorMessage(err)));
  }

  /** HttpErrorResponse není `instanceof Error` — jinak by UI vidělo jen „Chyba API“. */
  private extractApiErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const fromBody = this.messageFromHttpErrorBody(err.error);
      if (fromBody) return fromBody;
      if (err.status === 0) return 'Server nedostupný (síť / CORS).';
      return `HTTP ${err.status}${err.statusText ? ': ' + err.statusText : ''}`;
    }
    if (err instanceof Error) return err.message;
    return 'Chyba API';
  }

  private messageFromHttpErrorBody(body: unknown): string | null {
    if (!body || typeof body !== 'object') return null;
    const o = body as Record<string, unknown>;
    const m = o['message'];
    if (typeof m === 'string' && m.trim()) return m;
    if (Array.isArray(m) && m.length) return m.map(String).join(' ');
    const err = o['error'];
    if (typeof err === 'string' && err.trim()) return err;
    return null;
  }
}
